"use client";

import React, { Fragment, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { formatPeriodoTorneioBR } from "../utils/date";
import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import ProfileCardTournament from "../components/ProfilecardTournament";
import {
  criarInscricaoTorneio,
  CriarInscricaoTorneioResponse,
  CriarInscricaoTorneioInput,
  InscricaoMutationError,
} from "../services/inscricaoTorneioService";
import { podeSelecionarCategoria } from "../utils/filaEspera";
import FilaEsperaConfirmModal from "../components/inscricao/FilaEsperaConfirmModal";
import InscricaoErroModal, { CODIGO_CATEGORIA_SEM_VAGAS_FILA_DESLIGADA } from "../components/inscricao/InscricaoErroModal";
import {
  criarPagamentoInscricao,
  PagamentoInscricao,
} from "../services/pagamentoInscricaoService";
import { TORNEIO_POR_ID, TorneioPorIdData, TorneioPorIdVars } from "../services/torneioService";
import { ATLETA_POR_ID, ATUALIZAR_ATLETA, AtletaData, AtletaVars } from "../services/atletaService";
import { ATUALIZAR_USUARIO, AtualizarUsuarioVars } from "../services/userService";
import { useQuery, useMutation } from "@apollo/client";
import { useAuth } from "../context/AuthContext";
import Load from "../components/Load";
import {
  SEGUIDOR_USUARIO_TORNEIO,
  CRIAR_SEGUIDOR_MUTATION,
  SeguidorUsuarioTorneioData,
  SeguidorUsuarioTorneioVars,
  CriarSeguidorResponse,
} from "../services/seguidorService";
import { StatusInscricao } from "../types/inscricao";
import InscriptionStatusChip from "../components/InscriptionStatusChip";

export interface InscricaoCheckout {
  categoria: { id: string; nome: string; valor: number };
  equipe: { id: string; nome: string }[];
}

interface LocationState {
  inscricoes: InscricaoCheckout[];
  torneioId: string;
  valorSemDesconto?: number;
  descontoAplicado?: number;
  valorComDesconto?: number;
}

const TournamentInscriptionCheckout: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: LocationState };
  const auth = useAuth();
  const user =
    (auth?.user as any) ||
    (typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("user") || "null")
      : null);
  const usuarioId = user?.id ?? "";
  const atletaId = user?.atleta?.id ?? "";

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [genero, setGenero] = useState("");
  const [tamanhoCamiseta, setTamanhoCamiseta] = useState("");
  const [tamanhoSapato, setTamanhoSapato] = useState<number | "">("");
  const [submitted, setSubmitted] = useState(false);
  const [initialData, setInitialData] = useState({ nome: "", email: "", telefone: "", genero: "", tamanhoCamiseta: "", tamanhoSapato: "" as number | "" });
  const [updating, setUpdating] = useState(false);
  const [updateMsg, setUpdateMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [profileRefresh, setProfileRefresh] = useState(0);

  const inscricoes = state?.inscricoes ?? [];
  const torneioId = state?.torneioId ?? "";
  
  const emailValid = /\S+@\S+\.\S+/.test(email);

  const { data, loading, error } = useQuery<TorneioPorIdData, TorneioPorIdVars>(TORNEIO_POR_ID, {
    variables: { id: torneioId },
    skip: !torneioId,
  });

  const { data: atletaData } = useQuery<AtletaData, AtletaVars>(ATLETA_POR_ID, {
    variables: { id: atletaId },
    skip: !atletaId,
  });

  const [mutAtualizarAtleta] = useMutation(ATUALIZAR_ATLETA);
  const [mutAtualizarUsuario] = useMutation<unknown, AtualizarUsuarioVars>(ATUALIZAR_USUARIO);

  // Verifica se o atleta já segue este torneio
  const { data: seguidorData } = useQuery<SeguidorUsuarioTorneioData, SeguidorUsuarioTorneioVars>(
    SEGUIDOR_USUARIO_TORNEIO,
    {
      variables: { seguidorId: atletaId, seguidoTorneioId: torneioId || "" },
      skip: !atletaId || !torneioId,
      fetchPolicy: "network-only",
    }
  );

  // Mutation para criar seguidor
  const [mutCriarSeguidor] = useMutation<{ criarSeguidor: CriarSeguidorResponse }>(CRIAR_SEGUIDOR_MUTATION);


  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successData, setSuccessData] = useState<CriarInscricaoTorneioResponse[]>([]);
  const [formSuccess, setFormSuccess] = useState("");
  const [formError, setFormError] = useState("");
  // Phase 4: estados dos modais (FilaEsperaConfirm + InscricaoErro)
  const [filaConfirmOpen, setFilaConfirmOpen] = useState(false);
  const [erroModal, setErroModal] = useState<{ open: boolean; codigo?: string; mensagem?: string }>({ open: false });
  const torneio = data?.torneioPorId;

  // Phase 4: categorias selecionadas que vão entrar em fila de espera
  // (vagas esgotadas + fila efetiva ativa). Lookup contra `torneio.categoriasTorneio`
  // porque `inscricoes` (LocationState) só carrega {id, nome, valor}.
  const categoriasFila: string[] = React.useMemo(() => {
    if (!torneio?.categoriasTorneio) return [];
    return inscricoes.reduce<string[]>((acc, ins) => {
      const catCompleta = torneio.categoriasTorneio?.find((c) => c.id === ins.categoria.id);
      if (catCompleta && podeSelecionarCategoria(catCompleta, torneio).iraParaFilaEspera) {
        acc.push(ins.categoria.nome);
      }
      return acc;
    }, []);
  }, [inscricoes, torneio]);

  const total = inscricoes.reduce((sum, i) => sum + i.categoria.valor, 0);

  useEffect(() => {
    if (atletaData?.atletaPorId) {
      const a = atletaData.atletaPorId;
      setNome(a.usuario.nome || "");
      setEmail(a.usuario.email || "");
      setTelefone(a.telefone || "");
      setGenero(a.genero || "");
      setTamanhoCamiseta(a.tamanhoCamiseta || "");
      setTamanhoSapato(a.tamanhoSapato ?? "");
      setInitialData({ nome: a.usuario.nome || "", email: a.usuario.email || "", telefone: a.telefone || "", genero: a.genero || "", tamanhoCamiseta: a.tamanhoCamiseta || "", tamanhoSapato: a.tamanhoSapato ?? "" });
    }
  }, [atletaData]);

  const renderFormError = (msg: string, atletaIdParam: string): React.ReactNode => {
    const precisaPerfil =
      msg.includes('gênero') ||
      msg.includes('genero') ||
      msg.includes('data de nascimento') ||
      msg.includes('perfil');
    if (precisaPerfil && atletaIdParam) {
      return (
        <span>
          {msg}{' '}
          <Link to="/accountinformation">Acessar perfil</Link>
        </span>
      );
    }
    return <span>{msg}</span>;
  };

  const handleFinish = async () => {
    setSaving(true);
    setFormError("");
    setFormSuccess("");
    setSuccessData([]);
    try {
      const created: CriarInscricaoTorneioResponse[] = [];
      const createdIds: string[] = [];
      const cidadeAtleta = atletaData?.atletaPorId?.cidade?.nome || "";
      for (const ins of inscricoes) {
        const input: CriarInscricaoTorneioInput = {
          categoriaTorneioId: ins.categoria.id,
          dataInscricao: new Date().toISOString().split('T')[0],
          equipe: {
            nome: "",
            cidade: cidadeAtleta,
            atletas: ins.equipe.map((e) => ({ id: e.id })),
          },
          observacao: "",
        };
        const res = await criarInscricaoTorneio(input);
        if (res) {
          created.push(res);
          createdIds.push(res.id);
        }
      }
      if (createdIds.length === 0) {
        throw new Error("Nenhuma inscrição criada");
      }
      const pagamento: PagamentoInscricao | undefined = await criarPagamentoInscricao(createdIds, {
        valorSemDesconto: state?.valorSemDesconto ?? total,
        descontoAplicado: state?.descontoAplicado ?? 0,
        valorComDesconto: state?.valorComDesconto ?? total,
      });
      if (!pagamento) {
        throw new Error("Erro ao criar pagamento");
      }
      // se o atleta não segue o torneio, cria o seguidor automaticamente
      try {
        if (atletaId && torneioId) {
          const jaSegue = !!seguidorData?.seguidorPorSeguidorIdSeguidoTorneioId?.id;
          if (!jaSegue) {
            await mutCriarSeguidor({
              variables: {
                input: {
                  seguidorId: atletaId,
                  seguidoAtletaId: null,
                  seguidoTorneioId: torneioId,
                },
              },
              refetchQueries: [{
                query: SEGUIDOR_USUARIO_TORNEIO,
                variables: { seguidorId: atletaId, seguidoTorneioId: torneioId }
              }],
              awaitRefetchQueries: true,
            });
            // Força o ProfilecardTournament a remontar e refazer a query
            setProfileRefresh((v) => v + 1);
          }
        }
      } catch (e) {
        // Não bloqueie o fluxo se falhar — apenas registra/ignora
        console.warn("Falha ao criar seguidor automático:", e);
      }
      setSuccessData(created);
      const hasFilaEspera = created.some((c) => c.status === StatusInscricao.FILA_ESPERA);
      const allFilaEspera = created.every((c) => c.status === StatusInscricao.FILA_ESPERA);
      if (allFilaEspera) {
        setFormSuccess("Inscrição(ões) adicionada(s) à fila de espera!");
      } else if (hasFilaEspera) {
        setFormSuccess("Inscrição realizada! Algumas categorias ficaram na fila de espera.");
      } else {
        setFormSuccess("Inscrição realizada com sucesso!");
      }
      if (pagamento.dataVencimento) {
        setFormSuccess((prev) => `${prev} Pagamento deve ser realizado em até 24 horas.`);
      }
      navigate(`/athlete/tournamentinscriptions/${pagamento.id}`);
    } catch (err) {
      // Phase 4: lê extensions.code propagado pelo service (InscricaoMutationError).
      // Roteamento: erro de capacidade (vagas/fila) → modal específico por código;
      // erros de elegibilidade/negócio → alerta inline (renderFormError adiciona o
      // link "Acessar perfil" quando o erro é de gênero/idade/perfil).
      if (err instanceof InscricaoMutationError && err.codigo === CODIGO_CATEGORIA_SEM_VAGAS_FILA_DESLIGADA) {
        setErroModal({ open: true, codigo: err.codigo, mensagem: err.message });
      } else if (err instanceof InscricaoMutationError) {
        // AC-17 (nivel-categoria-liga): este ramo genérico é o que satisfaz AC-17 — a
        // BusinessException de exclusividade entre CategoriaLigas (Phase 2) não carrega
        // `extensions.code` reconhecido, então cai aqui e `err.message` (a mensagem real
        // do backend, nomeando as duas categorias em conflito) chega ao atleta via
        // `setFormError`. Nenhuma mudança de comportamento — trava estrutural.
        setFormError(err.message);
      } else {
        setErroModal({ open: true, mensagem: "Erro ao realizar inscrição. Tente novamente." });
      }
    } finally {
      setSaving(false);
      setFilaConfirmOpen(false);
    }
  };

  // Phase 4: intercepta o submit — se há categorias indo pra fila, abre modal de confirmação.
  const handleFinishClick = () => {
    if (categoriasFila.length > 0) {
      setFilaConfirmOpen(true);
    } else {
      handleFinish();
    }
  };

  const handleUpdate = async () => {
    setSubmitted(true);
    if (!nome.trim() || !emailValid || !telefone.trim()) {
      return;
    }
    setUpdating(true);
    setUpdateMsg(null);
    try {
      if (usuarioId) {
        await mutAtualizarUsuario({ variables: { id: usuarioId, nome, email, username: user.username } });
        if (user) {
          const newUser = { ...user, nome, email, username: user.username };
          localStorage.setItem("user", JSON.stringify(newUser));
        }
      }
      if (atletaId) {
        await mutAtualizarAtleta({
          variables: {
            id: atletaId,
            telefone,
            genero: genero || null,
            tamanhoCamiseta: tamanhoCamiseta || null,
            tamanhoSapato: tamanhoSapato === "" ? undefined : Number(tamanhoSapato),
          },
        });
      }
      setInitialData({ nome, email, telefone, genero, tamanhoCamiseta, tamanhoSapato });
      setUpdateMsg({ type: "success", text: "Dados atualizados com sucesso." });
    } catch (err) {
      setUpdateMsg({ type: "error", text: "Erro ao atualizar dados." });
    } finally {
      setUpdating(false);
    }
  };

  if (!state) {
    return <p className="m-3">Dados da inscrição não encontrados.</p>;
  }

  const dadosPessoaisForm = (
        <div className="card border-0 p-4">
            <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                Dados pessoais
            </h4>
            <div className="mb-3">
                <label className="font-xssss fw-600 text-grey-700">
                    Nome completo <span className="text-danger">*</span>
                </label>
                <input
                    type="text"
                    className="form-control"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                />
                {submitted && nome.trim() === "" && (
                    <small className="text-danger">
                        Campo obrigatório
                    </small>
                )}
            </div>
            <div className="mb-3">
                <label className="font-xssss fw-600 text-grey-700">
                    E-mail <span className="text-danger">*</span>
                </label>
                <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                {submitted && !emailValid && (
                    <small className="text-danger">E-mail inválido</small>
                )}
            </div>
            <div className="mb-3">
                <div className="row">
                  <div className="col-6">
                    <label className="font-xssss fw-600 text-grey-700">
                        Telefone <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={telefone}
                      onChange={(e) => setTelefone(e.target.value)}
                    />
                    {submitted && telefone.trim() === "" && (
                      <small className="text-danger">
                        Campo obrigatório
                      </small>
                    )}
                  </div>
                  <div className="col-6">
                    <label className="font-xssss fw-600 text-grey-700">
                      Gênero
                    </label>
                    <select
                      className="form-select form-control"
                      value={genero}
                      onChange={(e) => setGenero(e.target.value)}
                    >
                      <option value="">Selecione</option>
                      <option value="MASCULINO">Masculino</option>
                      <option value="FEMININO">Feminino</option>
                      <option value="OUTRO">Outro</option>
                    </select>
                  </div>
                  {torneio?.camisetas && (
                    <div className="col-6 mt-3">
                      <label className="font-xssss fw-600 text-grey-700">
                        Tamanho da camiseta <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select form-control"
                        value={tamanhoCamiseta}
                        onChange={(e) => setTamanhoCamiseta(e.target.value)}
                      >
                        <option value="PP">PP</option>
                        <option value="P">P</option>
                        <option value="M">M</option>
                        <option value="G">G</option>
                        <option value="GG">GG</option>
                      </select>
                      {submitted && tamanhoCamiseta === "" && (
                        <small className="text-danger">Campo obrigatório</small>
                      )}
                    </div>
                  )}
                  {torneio?.sapatos && (
                    <div className="col-6 mt-3">
                      <label className="font-xssss fw-600 text-grey-700">
                        Tamanho do sapato
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={tamanhoSapato}
                        onChange={(e) => setTamanhoSapato(e.target.value === '' ? '' : Number(e.target.value))}
                      />
                    </div>
                  )}
                </div>
                {submitted && telefone.trim() === "" && (
                    <small className="text-danger">
                        Campo obrigatório
                    </small>
                )}
            </div>
        </div>
    );

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">
            <div className="row">
              <div className="col-xl-12 col-xxl-12 col-lg-12">
                {loading && <Load />}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    Erro ao carregar inscrição.
                  </div>
                )}
                {torneio && <ProfileCardTournament key={profileRefresh} torneio={torneio} />}
              </div>
            </div>
            <div className="row bg-white rounded-xxl shadow-xs p-4 m-1 mb-4">
              <div className="row">
                <div className="col-xl-12 col-xxl-12 col-lg-12 card-body">
                    <div className="row">
                      <div className="col-xl-1">
                        <figure className="avatar w50 me-auto">
                          <img
                            src={torneio?.avatarUrl || "/assets/images/user.png"}
                            alt={torneio?.nome || "avatar"}
                            className="p-1 bg-white rounded-xl w-100"
                          />
                        </figure>
                      </div>
                      <div className="col-xl-11">
                        <h5 id='subtitle-tournament' className="font-xs ls-1 fw-700 text-grey-900">
                          {torneio?.subtitulo || torneio?.nome}
                          <span id='date-tournament' className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">
                            {formatPeriodoTorneioBR(torneio?.dataInicio, torneio?.dataFim)}{" "}
                            {torneio?.local?.endereco?.cidade && (
                              <span id='city-uf-tournament' className="text-primary">
                                {torneio.local.endereco.cidade.nome},{torneio.local.endereco.cidade.uf}
                              </span>
                            )}
                          </span>
                        </h5>
                      </div>
                      <hr />
                    </div>
                </div>
                {formError && (
                  <div className="alert alert-danger">
                    {renderFormError(formError, atletaId)}
                  </div>
                )}
                {formSuccess && (
                  <div className="alert alert-success">
                    {formSuccess}
                    {successData.length > 0 && (
                      <ul className="mt-2 mb-0">
                        {successData.map((s) => (
                          <li key={s.id}>
                            {s.categoriaTorneio.nome} - {s.equipe.nome}
                            {s.status === StatusInscricao.FILA_ESPERA && (
                              <span className="ms-2">
                                <InscriptionStatusChip status={s.status} />
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="col-xl-6 col-xxl-6 col-lg-6 card-body">
                  {dadosPessoaisForm}
                  {updateMsg && (
                    <div
                      className={`alert alert-${updateMsg.type === 'success' ? 'success' : 'danger'} mt-3`}
                      role="alert"
                    >
                      {updateMsg.text}
                    </div>
                  )}
                  <div className="d-flex justify-content-end mt-3">
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdate}
                      disabled={
                        updating ||
                        nome === initialData.nome &&
                        email === initialData.email &&
                        telefone === initialData.telefone &&
                        genero === initialData.genero &&
                        tamanhoCamiseta === initialData.tamanhoCamiseta &&
                        tamanhoSapato === initialData.tamanhoSapato
                      }
                    >
                      {updating ? 'Atualizando...' : 'Atualizar dados'}
                    </button>
                  </div>
                </div>
                <div className="col-xl-6 col-xxl-6 col-lg-6 card-body">
                  {/* Dados da equipe não são mais coletados nesta etapa */}
                  <div className="card border-0 p-4">

                    <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                      Detalhes da inscrição
                    </h4>

                    <table className="table">
                      <thead>
                        <tr>
                          <th>Categoria</th>
                          <th>Equipe</th>
                          <th className="text-end">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inscricoes.map((i) => (
                          <tr key={i.categoria.id}>
                            <td>{i.categoria.nome}</td>
                            <td>{i.equipe.map((e) => e.nome).join(" / ")}</td>
                            <td className="text-end">R$ {i.categoria.valor.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan={2}>Subtotal</th>
                          <th className="text-end">R$ {(state?.valorSemDesconto ?? total).toFixed(2)}</th>
                        </tr>
                        <tr>
                          <th colSpan={2}>Descontos</th>
                          <th className="text-end">- R$ {(state?.descontoAplicado ?? 0).toFixed(2)}</th>
                        </tr>
                        <tr>
                          <th colSpan={2}>Total</th>
                          <th className="text-end">R$ {(state?.valorComDesconto ?? total).toFixed(2)}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <button className="btn btn-light me-2" onClick={() => navigate(-1)} disabled={saving}>
                    Voltar
                  </button>
                  <button className="btn bg-primary text-white" onClick={() => setConfirmDialogOpen(true)} disabled={saving}>
                    {saving ? 'Enviando...' : 'Finalizar inscrição'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Consentimento de compartilhamento de dados — sempre exibido antes de finalizar */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar inscrição</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Ao finalizar sua inscrição, você concorda em compartilhar suas
            informações de <strong>e-mail</strong> e <strong>telefone</strong> com
            a organização do torneio para fins de comunicação e gestão do evento.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              // Após o consentimento, aplica o gate de fila de espera (Phase 4)
              handleFinishClick();
            }}
            variant="contained"
            disabled={saving}
          >
            Concordo e finalizar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Phase 4: modal de confirmação quando alguma categoria selecionada vai entrar em fila */}
      <FilaEsperaConfirmModal
        open={filaConfirmOpen}
        categoriasFila={categoriasFila}
        loading={saving}
        onCancel={() => setFilaConfirmOpen(false)}
        onConfirm={handleFinish}
      />
      {/* Phase 4: modal de erro (substitui alert-danger inline) */}
      <InscricaoErroModal
        open={erroModal.open}
        codigo={erroModal.codigo}
        mensagem={erroModal.mensagem}
        onClose={() => setErroModal({ open: false })}
      />
    </Fragment>
  );
};

export default TournamentInscriptionCheckout;
